/**
 * Ordinary least-squares line for a scatter plot's trend line.
 */

import { MINUS, type NumberFormat } from "@/lib/tool-kit/charts/format";

export interface LinearFit {
  /** b in ŷ = a + bx. */
  slope: number;
  /** a in ŷ = a + bx. */
  intercept: number;
  /** Pearson's r. NaN when every y is the same. */
  r: number;
  /** Coefficient of determination, r². NaN when every y is the same. */
  r2: number;
  n: number;
}

/**
 * Fit ŷ = a + bx by least squares: b = Sxy / Sxx and a = ȳ − b·x̄. Null with fewer than
 * two points or when every x is the same, since a vertical line has no slope.
 */
export function linearFit(
  xs: readonly number[],
  ys: readonly number[]
): LinearFit | null {
  const n = Math.min(xs.length, ys.length);
  if (n < 2) {
    return null;
  }
  let sumX = 0;
  let sumY = 0;
  for (let i = 0; i < n; i++) {
    sumX += xs[i];
    sumY += ys[i];
  }
  const meanX = sumX / n;
  const meanY = sumY / n;
  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
  }
  if (sxx === 0) {
    return null;
  }
  const slope = sxy / sxx;
  const r = syy === 0 ? Number.NaN : sxy / Math.sqrt(sxx * syy);
  return { slope, intercept: meanY - slope * meanX, r, r2: r * r, n };
}

/** A coefficient with four significant digits (one decimal past a thousand). */
export function formatCoefficient(fmt: NumberFormat, value: number): string {
  return Math.abs(value) >= 1000
    ? fmt(value, { maximumFractionDigits: 1 })
    : fmt(value, { maximumSignificantDigits: 4 });
}

/** "y = 2.5x + 3.1" — notation, written the same in every language. */
export function formatEquation(fit: LinearFit, fmt: NumberFormat): string {
  const slope = formatCoefficient(fmt, fit.slope).replace("-", MINUS);
  const sign = fit.intercept < 0 ? MINUS : "+";
  return `y = ${slope}x ${sign} ${formatCoefficient(fmt, Math.abs(fit.intercept))}`;
}

/** r² to three decimals, or a minus sign when it is undefined (every y the same). */
export function formatR2(fit: LinearFit, fmt: NumberFormat): string {
  return Number.isNaN(fit.r2)
    ? MINUS
    : fmt(fit.r2, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
